if (!customElements.get('engraving-field')) {

  // Shopify's limits for files sent as line item properties
  const MAX_FILE_BYTES = 20 * 1024 * 1024;
  const MAX_PIXELS = 25000000;
  const MIN_SIDE = 1000;
  const ALLOWED_EXTENSIONS = ['svg', 'pdf', 'png', 'jpg', 'jpeg'];
  const RASTER_EXTENSIONS = ['png', 'jpg', 'jpeg'];

  /**
   *  @class
   *  @function EngravingField
   */
  class EngravingField extends HTMLElement {
    connectedCallback() {
      this.strings = (window.theme && window.theme.engravingStrings) || {};
      this.max = parseInt(this.dataset.max, 10) || 30;

      this.toggle = this.querySelector('[data-engraving-toggle]');
      this.panel = this.querySelector('[data-engraving-panel]');
      this.modes = Array.from(this.querySelectorAll('[data-engraving-mode]'));
      this.textPanel = this.querySelector('[data-engraving-panel-text]');
      this.imagePanel = this.querySelector('[data-engraving-panel-image]');
      this.textInput = this.querySelector('[data-engraving-text]');
      this.counter = this.querySelector('[data-engraving-count]');
      this.fileInput = this.querySelector('[data-engraving-file]');
      this.fileName = this.querySelector('[data-engraving-file-name]');
      this.fileStatus = this.querySelector('[data-engraving-file-status]');
      this.preview = this.querySelector('[data-engraving-preview]');
      this.form = this.textInput && this.textInput.form;
      this.section = this.closest('.shopify-section') || document;

      this.fonts = Array.from(this.querySelectorAll('[data-engraving-font]'));
      this.fontSamples = Array.from(this.querySelectorAll('[data-engraving-font-sample]'));
      this.mockup = this.querySelector('[data-engraving-mockup]');
      this.zone = this.querySelector('[data-engraving-zone]');
      this.zoneText = this.querySelector('[data-engraving-zone-text]');
      this.zoneImage = this.querySelector('[data-engraving-zone-image]');
      this.fonts.forEach((radio) => radio.addEventListener('change', this.updatePreview.bind(this)));
      if (this.zone && 'ResizeObserver' in window) {
        new ResizeObserver(() => this.fitText()).observe(this.zone);
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fitText());
      // Web fonts load on first use and are wider than the fallback: refit once they arrive
      if (document.fonts && document.fonts.addEventListener) {
        document.fonts.addEventListener('loadingdone', () => { this.fitText(); this.fitSamples(); });
      }
      this.setupVariantImages();
      this.zoomToggle = this.querySelector("[data-engraving-zoom-toggle]");
      this.stage = this.querySelector("[data-engraving-stage]");
      if (this.zoomToggle && this.stage) {
        this.zoomToggle.addEventListener("click", () => {
          const zoomed = this.stage.classList.toggle("is-zoomed");
          this.zoomToggle.setAttribute("aria-pressed", zoomed);
          this.zoomToggle.textContent = zoomed ? this.zoomToggle.dataset.labelZoomed : this.zoomToggle.dataset.labelFull;
        });
      }

      this.toggle.addEventListener('change', this.render.bind(this));
      this.modes.forEach((radio) => radio.addEventListener('change', this.render.bind(this)));
      this.textInput.addEventListener('input', this.updateText.bind(this));
      this.textInput.addEventListener('blur', () => {
        this.textInput.value = this.textInput.value.trim();
        this.updateText();
      });
      if (this.fileInput) this.fileInput.addEventListener('change', this.onFileChange.bind(this));

      // An engraving that is only spaces should not reach the cart.
      if (this.form) {
        this.onFormData = (evt) => {
          if (this.textInput.disabled) return;
          evt.formData.set(this.textInput.name, this.textInput.value.trim());
        };
        this.form.addEventListener('formdata', this.onFormData);
      }

      this.render();

      if (this.dataset.designMode === 'true') this.setupZoneTool();
    }

    /**
     * Theme editor only: drag and resize the engraving zone on the photo and
     * get the "left,top,width,height,rotation" code to paste in the block.
     */
    setupZoneTool() {
      const tool = this.querySelector('[data-zone-tool]');
      if (!this.zone || !this.stage || !tool) return;

      // Show the panel and the whole product so the zone can be placed anywhere
      this.toggle.checked = true;
      this.render();
      this.stage.classList.remove('is-zoomed');
      if (this.zoomToggle) this.zoomToggle.hidden = true;
      this.zone.classList.add('is-editing');

      const code = tool.querySelector('[data-zone-code]');
      const rotate = tool.querySelector('[data-zone-rotate]');
      const rotateNumber = tool.querySelector('[data-zone-rotate-number]');
      const copy = tool.querySelector('[data-zone-copy]');
      const round = (n) => Math.round(n * 10) / 10;
      const box = {
        x: parseFloat(this.zone.style.left) || 0,
        y: parseFloat(this.zone.style.top) || 0,
        w: parseFloat(this.zone.style.width) || 20,
        h: parseFloat(this.zone.style.height) || 10,
        r: parseFloat(rotate.value) || 0
      };

      const apply = () => {
        box.w = Math.min(Math.max(box.w, 2), 100);
        box.h = Math.min(Math.max(box.h, 2), 100);
        box.x = Math.min(Math.max(box.x, 0), 100 - box.w);
        box.y = Math.min(Math.max(box.y, 0), 100 - box.h);
        Object.assign(this.zone.style, {
          left: box.x + '%', top: box.y + '%', width: box.w + '%', height: box.h + '%',
          transform: `rotate(${box.r}deg)`
        });
        code.textContent = [box.x, box.y, box.w, box.h, box.r].map(round).join(',');
        rotate.value = box.r;
        if (document.activeElement !== rotateNumber) rotateNumber.value = round(box.r);
        this.fitText();
      };

      rotate.addEventListener('input', () => { box.r = parseFloat(rotate.value) || 0; apply(); });
      rotateNumber.addEventListener('input', () => {
        const value = parseFloat(rotateNumber.value);
        if (Number.isNaN(value)) return;
        box.r = Math.min(Math.max(value, -180), 180);
        apply();
      });

      this.zone.addEventListener('pointerdown', (evt) => {
        evt.preventDefault();
        const handle = evt.target.dataset.zoneHandle || 'move';
        const rect = this.stage.getBoundingClientRect();
        const start = { px: evt.clientX, py: evt.clientY, ...box };
        this.zone.setPointerCapture(evt.pointerId);

        const onMove = (e) => {
          const dx = (e.clientX - start.px) / rect.width * 100;
          const dy = (e.clientY - start.py) / rect.height * 100;
          if (handle === 'move') {
            box.x = start.x + dx;
            box.y = start.y + dy;
          } else {
            // Resize along the box's own (rotated) axes, keeping its centre in place
            const rad = -start.r * Math.PI / 180;
            const dxPx = e.clientX - start.px;
            const dyPx = e.clientY - start.py;
            const localX = dxPx * Math.cos(rad) - dyPx * Math.sin(rad);
            const localY = dxPx * Math.sin(rad) + dyPx * Math.cos(rad);
            const signX = handle.includes('e') ? 1 : -1;
            const signY = handle.includes('s') ? 1 : -1;
            const centerX = start.x + start.w / 2;
            const centerY = start.y + start.h / 2;
            box.w = (start.w / 100 * rect.width + 2 * signX * localX) / rect.width * 100;
            box.h = (start.h / 100 * rect.height + 2 * signY * localY) / rect.height * 100;
            box.x = centerX - box.w / 2;
            box.y = centerY - box.h / 2;
          }
          apply();
        };
        const onUp = () => {
          this.zone.removeEventListener('pointermove', onMove);
          this.zone.removeEventListener('pointerup', onUp);
          this.zone.removeEventListener('pointercancel', onUp);
        };
        this.zone.addEventListener('pointermove', onMove);
        this.zone.addEventListener('pointerup', onUp);
        this.zone.addEventListener('pointercancel', onUp);
      });

      copy.addEventListener('click', () => {
        const label = copy.querySelector('span');
        const done = () => {
          const original = label.textContent;
          label.textContent = copy.dataset.copied || original;
          setTimeout(() => { label.textContent = original; }, 1500);
        };
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code.textContent).then(done).catch(() => {});
        } else {
          const range = document.createRange();
          range.selectNodeContents(code);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        }
      });

      apply();
    }

    disconnectedCallback() {
      if (this.form && this.onFormData) this.form.removeEventListener('formdata', this.onFormData);
      this.setDynamicCheckoutHidden(false);
    }

    get mode() {
      const checked = this.modes.find((radio) => radio.checked);
      return checked ? checked.value : 'text';
    }

    render() {
      const enabled = this.toggle.checked;
      const mode = this.mode;

      this.toggle.setAttribute('aria-expanded', enabled);
      this.panel.hidden = !enabled;
      this.textPanel.hidden = mode !== 'text';
      if (this.imagePanel) this.imagePanel.hidden = mode !== 'image';

      // Disabled inputs are left out of the cart request, so only the chosen option is sent.
      this.textInput.disabled = !enabled || mode !== 'text';
      this.textInput.required = enabled && mode === 'text';
      if (this.fileInput) {
        this.fileInput.disabled = !enabled || mode !== 'image';
        this.fileInput.required = enabled && mode === 'image';
      }
      // The chosen font only travels with a text engraving
      this.fonts.forEach((radio) => { radio.disabled = !enabled || mode !== 'text'; });

      // "Buy it now" skips the theme's cart request and cannot carry the engraving.
      this.setDynamicCheckoutHidden(enabled);

      this.updateText();
      this.validateFile();
    }

    updateText() {
      const length = Array.from(this.textInput.value).length;
      const remaining = Math.max(this.max - length, 0);

      if (this.counter) {
        this.counter.textContent = remaining === 1 && this.strings.remainingOne
          ? this.strings.remainingOne
          : (this.strings.remaining || '[n]').replace('[n]', remaining);
        this.counter.classList.toggle('is-limit', remaining === 0);
      }

      if (this.textInput.required && !this.textInput.value.trim()) {
        this.textInput.setCustomValidity(this.strings.requiredText || '');
      } else {
        this.textInput.setCustomValidity('');
      }

      this.updatePreview();
    }

    // The preview photo follows the chosen variant (each colour has its own photo)
    setupVariantImages() {
      const json = this.querySelector('[data-engraving-variant-images]');
      const img = this.querySelector('[data-engraving-preview-image]');
      const idInput = this.form && this.form.querySelector('input[name="id"]');
      if (!json || !img || !idInput) return;
      let images;
      try { images = JSON.parse(json.textContent); } catch (e) { return; }

      // The theme updates this input once the new variant is resolved
      idInput.addEventListener('change', () => {
        const image = images[idInput.value];
        if (!image || !image.src || img.getAttribute('src') === image.src) return;
        img.srcset = image.srcset;
        img.src = image.src;
        if (image.width) img.width = image.width;
        if (image.height) img.height = image.height;
      });
    }

    get selectedFontFamily() {
      const checked = this.fonts.find((radio) => radio.checked) || this.fonts[0];
      return checked ? checked.dataset.fontFamily : '';
    }

    updatePreview() {
      const typed = this.textInput.value.trim();
      const sample = this.zoneText ? this.zoneText.dataset.sample : '';

      // Each font option shows the customer's own text
      this.fontSamples.forEach((el) => { el.textContent = typed || sample; });
      this.fitSamples();

      if (!this.zone) return;
      const imageMode = this.mode === 'image';

      this.zoneText.hidden = imageMode;
      this.zoneImage.hidden = !imageMode || !this.zoneImage.getAttribute('src');

      if (!imageMode) {
        this.zoneText.textContent = typed || sample;
        this.zoneText.classList.toggle('is-sample', !typed);
        const family = this.selectedFontFamily;
        this.zoneText.style.fontFamily = family;
        this.fitText();
        if (document.fonts && document.fonts.load) {
          document.fonts.load(`16px ${family}`, this.zoneText.textContent).then(() => this.fitText()).catch(() => {});
        }
      }
    }

    // Each font card shrinks its sample so the whole text is visible
    fitSamples() {
      this.fontSamples.forEach((el) => {
        el.style.fontSize = '';
        const max = el.parentElement.clientWidth - 12;
        if (max <= 0) return;
        let size = parseFloat(getComputedStyle(el).fontSize);
        while (el.scrollWidth > max && size > 7) {
          size -= 0.5;
          el.style.fontSize = size + 'px';
        }
      });
    }

    // Largest font size that keeps the text on one line inside the zone
    fitText() {
      if (!this.zone || !this.zoneText || this.zoneText.hidden) return;
      const width = this.zone.clientWidth;
      const height = this.zone.clientHeight;
      if (!width || !height) return;
      let size = height * 0.9;
      this.zoneText.style.fontSize = size + 'px';
      // Keep a small margin: script fonts draw swashes outside their measured box
      while (this.zoneText.scrollWidth > width * 0.92 && size > 6) {
        size *= 0.92;
        this.zoneText.style.fontSize = size + 'px';
      }
    }

    onFileChange() {
      this.fileError = '';
      this.clearPreview();
      const file = this.fileInput.files[0];

      if (!file) {
        this.fileName.textContent = this.strings.noFile || '';
        this.validateFile();
        return;
      }

      this.fileName.textContent = file.name;
      const extension = file.name.split('.').pop().toLowerCase();

      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        this.fileError = this.strings.fileType;
      } else if (file.size > MAX_FILE_BYTES) {
        this.fileError = this.strings.fileTooLarge;
      }

      if (this.fileError || !RASTER_EXTENSIONS.includes(extension)) {
        if (!this.fileError && extension === 'svg') this.showPreview(file);
        this.validateFile();
        return;
      }

      // PNG/JPG: check the real pixel size before accepting it.
      this.setStatus(this.strings.checking, false);
      this.fileInput.setCustomValidity(this.strings.checking || ' ');
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const { naturalWidth: w, naturalHeight: h } = img;
        if (w * h > MAX_PIXELS) {
          this.fileError = this.strings.imageTooManyPixels;
        } else if (Math.min(w, h) < MIN_SIDE) {
          this.fileError = (this.strings.imageTooSmall || '').replace('[w]', w).replace('[h]', h);
        }
        URL.revokeObjectURL(url);
        if (!this.fileError) this.showPreview(file);
        this.validateFile();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        this.fileError = this.strings.fileType;
        this.validateFile();
      };
      img.src = url;
    }

    validateFile() {
      if (!this.fileInput) return;
      let message = '';
      if (this.fileInput.required) {
        if (this.fileError) {
          message = this.fileError;
        } else if (!this.fileInput.files.length) {
          message = this.strings.requiredImage || '';
        }
      }
      this.fileInput.setCustomValidity(message);

      if (this.fileError) {
        this.setStatus(this.fileError, true);
      } else if (this.fileInput.files.length) {
        this.setStatus(this.strings.fileOk, false);
      } else {
        this.setStatus('', false);
      }
    }

    setStatus(text, isError) {
      if (!this.fileStatus) return;
      this.fileStatus.textContent = text || '';
      this.fileStatus.classList.toggle('is-error', !!isError);
    }

    showPreview(file) {
      // Kept for the product mockup, so the object URL is not revoked
      const url = URL.createObjectURL(file);
      if (this.preview) {
        const img = document.createElement('img');
        img.alt = '';
        img.src = url;
        this.preview.replaceChildren(img);
        this.preview.hidden = false;
      }
      if (this.zoneImage) {
        this.zoneImage.src = url;
        this.updatePreview();
      }
    }

    clearPreview() {
      if (this.preview) {
        this.preview.replaceChildren();
        this.preview.hidden = true;
      }
      if (this.zoneImage) {
        if (this.zoneImage.src) URL.revokeObjectURL(this.zoneImage.src);
        this.zoneImage.removeAttribute('src');
        this.updatePreview();
      }
    }

    setDynamicCheckoutHidden(hidden) {
      this.section.querySelectorAll('.shopify-payment-button').forEach((el) => {
        el.classList.toggle('product-engraving--hide-dynamic', hidden);
      });
    }
  }
  customElements.define('engraving-field', EngravingField);
}
